import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import SidePanel from "layouts/SidePanel";
import { useLocation, useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";
import axios from "../../../../utils/axios";
import { useSnackbar } from "notistack";
import { PDFViewer, PDFDownloadLink, pdf } from "@react-pdf/renderer";
import PoPdfGenerator from "./PoPdfGenerator";
import { SettingsInputSvideoOutlined } from "@mui/icons-material";


const validationSchema = Yup.object().shape({
    // name: Yup.string().required("Required"),
    // deliveryAddress: Yup.string().required("Required"),
    // buyerName: Yup.string().required("Required"),
    // supplierName: Yup.string().required("Required"),
    // contactName: Yup.string().required("Required"),  
    // buyerMobile: Yup.string().required("Required"),
    // buyerEmail: Yup.string().email("Invalid email").required("Required"),
    buyerCompanyName: Yup.string().required("Company name is required"),
});

const PreserveEditedPaymentTerms = ({
    editedPaymentTermsRef,
    hasEditedPaymentTermsRef,
    setFieldValue,
    values,
}) => {
    useEffect(() => {
        if (!hasEditedPaymentTermsRef.current) return;
        if (values.paymentTerms === editedPaymentTermsRef.current) return;

        setFieldValue("paymentTerms", editedPaymentTermsRef.current, false);
    }, [editedPaymentTermsRef, hasEditedPaymentTermsRef, setFieldValue, values.paymentTerms]);

    return null;
};

const PurchaseOrderForm = ({ }) => {
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const dropdownRef = useRef(null);
    const editedPaymentTermsRef = useRef("");
    const hasEditedPaymentTermsRef = useRef(false);
    const editedMaterialDescriptionsRef = useRef({});
    const { rfqData, poDataToUpdate } = location.state || {};
    const navigate = useNavigate();
    const isEditing = !!poDataToUpdate;
    const auth = useSelector((state) => state.auth);
    const user = auth?.user;
    const companyNames = user?.userEntity?.companies?.map((company) => company.name) || [];
    const lastQuotationSet = rfqData?.suppliers?.[0]?.vendorQuotations?.quotationSets?.slice(-1)[0];
    const [query, setQuery] = useState(isEditing ? poDataToUpdate?.buyerCompanyName : "");
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isPDFViewerOpen, setIsPDFViewerOpen] = useState(false);
    const [poData, setPoData] = useState(null);
    const [companyStampSign, setCompanyStampSign] = useState("");
    const [contactName, setContactName] = useState(rfqData?.suppliers[0]?.contactDetails[0]?.name);
    const [termsAndConditions, setTermsAndConditions] = useState(rfqData?.termsAndConditions || "");
    const [poNumberPrefix, setpoNumberPrefix] = useState("");
    const [supplierGst, setSupplierGst] = useState(""); // Added state for supplierGST
    const [supplierAddress, setSupplierAddress] = useState("");
    const [deliveryAddress, setDeliveryAddress] = useState("");
    const [deliveryDate, setDeliveryDate] = useState(rfqData?.deliveryDate);
    const [buyerName, setBuyerName] = useState("");
    const [buyerMobile, setBuyerMobile] = useState("");
    const [poSubject, setPoSubject] = useState(poDataToUpdate ? poDataToUpdate.poSubject : (rfqData?.name || ""));
    const [poId, setPoId] = useState(poDataToUpdate ? poDataToUpdate.poId : "");
    const [buyerCompanyAddress, setBuyerCompanyAddress] = useState(poDataToUpdate ? poDataToUpdate.buyerCompanyAddress : "");
    const [buyerCompanyGst, setBuyerCompanyGst] = useState(poDataToUpdate ? poDataToUpdate.buyerCompanyGst : "");

    useEffect(() => {
        hasEditedPaymentTermsRef.current = false;
        editedPaymentTermsRef.current = poDataToUpdate?.paymentTerms || rfqData?.paymentTerms || "";
    }, [poDataToUpdate?.paymentTerms, rfqData?.paymentTerms]);

    useEffect(() => {
        editedMaterialDescriptionsRef.current = {};
    }, [poDataToUpdate?.poId, rfqData?.rfqId]);


    const filteredCompanies = companyNames
        .filter((company) => company.toLowerCase().includes(query.toLowerCase()))
        .sort((a, b) => {
            if (a.toLowerCase() === query.toLowerCase()) return -1;
            if (b.toLowerCase() === query.toLowerCase()) return 1;
            return 0;
        });

    const handleSelect = async (company, setFieldValue) => {
        setQuery(company);
        setShowDropdown(false);
        const selectedCompany = user?.userEntity?.companies?.find(c => c.name === company);

        if (selectedCompany) {
            setFieldValue("buyerCompanyName", company);
            setBuyerCompanyAddress(selectedCompany.address);
            setBuyerCompanyGst(selectedCompany.gst);
            setFieldValue("companyStampSign", selectedCompany.stampAndSign);
            setCompanyStampSign(selectedCompany.stampAndSign || "");
            setpoNumberPrefix(selectedCompany.poNumberPrefix || "");

            // Fetch the latest PO ID when the company is selected
            if (selectedCompany.poNumberPrefix) {
                try {
                    const response = await axios.get(`/api/rfqs/purchase-orders/latest-po-id?poNumberPrefix=${selectedCompany.poNumberPrefix}&lastPoNumber=${selectedCompany.lastPoNumber}`);
                    if (response.data && !response.data.error) {
                        // Set the fetched PO ID
                        setPoId(response.data.newPoId);
                    } else {
                        enqueueSnackbar("Failed to fetch the latest PO ID", { variant: "error" });
                    }
                } catch (error) {
                    enqueueSnackbar("Error fetching latest PO ID", { variant: "error" });
                }
            }
        }
    };


    const handleClosePDFViewer = () => {
        setIsPDFViewerOpen(false);
    };

    const getInitialValuesForNewPO = (rfqData) => {
        const lastQuotationSet = rfqData?.suppliers?.[0]?.vendorQuotations?.quotationSets?.slice(-1)[0];
        const otherCharges = rfqData?.otherCharges || {};
        const charges = {};

        // Add selectedMrp for only selected charges
        if (otherCharges.transport && lastQuotationSet?.otherCharges?.transport?.selectedMrp > 0) {
            charges.transport = lastQuotationSet?.otherCharges?.transport?.selectedMrp || 0;
        }
        if (otherCharges.loading && lastQuotationSet?.otherCharges?.loading?.selectedMrp > 0) {
            charges.loading = lastQuotationSet?.otherCharges?.loading?.selectedMrp || 0;
        }
        if (otherCharges.unLoading && lastQuotationSet?.otherCharges?.unLoading?.selectedMrp > 0) {
            charges.unLoading = lastQuotationSet?.otherCharges?.unLoading?.selectedMrp || 0;
        }
        if (otherCharges.mathadeCharges && lastQuotationSet?.otherCharges?.mathadeCharges?.selectedMrp > 0) {
            charges.mathadeCharges = lastQuotationSet?.otherCharges?.mathadeCharges?.selectedMrp || 0;
        }
        if (otherCharges.miscCharges && lastQuotationSet?.otherCharges?.miscCharges?.selectedMrp > 0) {
            charges.miscCharges = lastQuotationSet?.otherCharges?.miscCharges?.selectedMrp || 0;
        }

        const quotationMaterialMap = (lastQuotationSet?.materialDetails || []).reduce((map, material) => {
            if (material.id) {
                map[material.id.toString()] = material;
            }
            return map;
        }, {});

        const mergedMaterials = (rfqData?.materials || []).map(material => {
            const matchedMaterial = quotationMaterialMap[material._id?.toString()];
            const selectedQuantity = matchedMaterial?.selectedQuantity ?? material.quantity;

            return matchedMaterial
                ? {
                    name: matchedMaterial.name,
                    quantity: selectedQuantity,
                    rfqQuantity: material.quantity,
                    selectedQuantity,
                    unit: matchedMaterial.unit || material.unit,
                    id: matchedMaterial.id,
                    specifications: matchedMaterial.specifications || material.specifications,
                    brand: material.brands,
                    basePrice: matchedMaterial.basePrice || 0,
                    discount: matchedMaterial.discount || 0,
                    gst: matchedMaterial.gst || 0,
                    totalCost: matchedMaterial.totalCost || 0,
                    totalExclGst: matchedMaterial.priceWithoutGst || 0,
                    isSelected: matchedMaterial.isSelected,
                    selectedItemRate: matchedMaterial.selectedItemRate,
                }
                : material;
        });

        const selectedMaterials = mergedMaterials.filter(mat => mat.isSelected === true);
        const materialTotalExclGST = selectedMaterials.reduce((acc, material) => {
            // Ensure rate and quantity are valid before multiplying
            const rate = material?.selectedItemRate ? material?.selectedItemRate : material.basePrice - (material.basePrice * material.discount) / 100; // Apply discount to rate
            return acc + (rate * material.quantity);
        }, 0) || 0;

        const materialTotalInclGST = selectedMaterials.reduce((acc, material) => {
            const rate = material?.selectedItemRate ? material?.selectedItemRate : material.basePrice - (material.basePrice * material.discount) / 100;
            const gstRate = material.gst || 0;
            return acc + (rate * material.quantity * (1 + gstRate / 100)); // Apply GST to rate * quantity
        }, 0) || 0;

        // Calculating totalExclGst and totalInclGst
        const totalExclGst = materialTotalExclGST + Object.values(charges).reduce((acc, charge) => acc + charge, 0);
        const totalInclGst = materialTotalInclGST + Object.entries(charges).reduce((acc, [key, charge]) => {
            const chargeGst = lastQuotationSet?.otherCharges?.[key]?.gst || 0;
            return acc + (charge * (1 + chargeGst / 100));
        }, 0);

        const initialValues = {
            rfqId: rfqData.rfqId,
            entityId: rfqData.entityId,
            poId: poId || "",
            poDate: new Date().toLocaleDateString('en-GB').replace(/\//g, '-'),
            poSubject: poSubject,
            name: rfqData?.name || "",
            supplierAddress: supplierAddress || "",
            supplierGst: supplierGst || "",
            supplierName: rfqData?.suppliers[0]?.businessName || "",
            contactName: contactName ? contactName: "",
            supplierMobile: rfqData?.suppliers[0]?.contactDetails[0]?.mobile || "",
            supplierEmail: rfqData?.suppliers[0]?.contactDetails[0]?.email || "",
            buyerCompanyName: query || "",
            projectName: rfqData?.projectName || "",
            deliveryAddress: deliveryAddress || "",
            deliveryDate: deliveryDate || "",
            buyerName: buyerName || "",
            buyerMobile: buyerMobile || "",
            buyerEmail: rfqData?.requestedBy || "",
            buyerCompanyAddress: buyerCompanyAddress || "",
            buyerCompanyGst: buyerCompanyGst || "",
            termsAndConditions: termsAndConditions || "",
            paymentTerms: hasEditedPaymentTermsRef.current
                ? editedPaymentTermsRef.current
                : rfqData?.paymentTerms || "",
            otherCharges: charges,
            companyStampSign: companyStampSign,
            preparedBy: user?.name,
            materialTotalExclGST,
            materialTotalInclGST,
            totalExclGst,
            totalInclGst,
            materials: selectedMaterials?.filter(mat => {
              // Calculate the rate first
              const rate = mat?.selectedItemRate 
                ? mat?.selectedItemRate 
                : mat?.basePrice - (mat?.basePrice * mat?.discount) / 100;
              // Only include materials with rate > 0
              return rate > 0;
            })?.map((mat, index) => ({
                name: Object.prototype.hasOwnProperty.call(
                    editedMaterialDescriptionsRef.current,
                    index,
                )
                    ? editedMaterialDescriptionsRef.current[index]
                    : `${mat?.name || ""}\n${mat?.specifications || ""}${mat?.brand?.length > 0 ? `\n${mat.brand.length === 1 ? "Brand" : "Brands"}: ${mat.brand.join(", ")}` : ""}`,
                quantity: mat?.selectedQuantity ?? mat?.quantity ?? 0,
                unit: mat?.unit || "",
                // rate: mat.basePrice - (mat.basePrice * mat.discount) / 100 || "",
                rate: mat?.selectedItemRate ? mat?.selectedItemRate : mat.basePrice - (mat.basePrice * mat.discount) / 100 || 0,
                gst: mat?.gst || 0,
                totalExclGst: mat?.selectedItemRate
                    ? mat.selectedItemRate * (mat?.selectedQuantity ?? mat?.quantity ?? 0)
                    : ((mat.basePrice - (mat.basePrice * mat.discount) / 100) * (mat?.selectedQuantity ?? mat?.quantity ?? 0)) || 0,
            })) || [],
        };

        return initialValues;
    };

    const getInitialValues = (rfqData, poDataToUpdate) => {
        if (poDataToUpdate) {
            return {
                rfqId: poDataToUpdate.rfqId,
                entityId: poDataToUpdate.entityId,
                poId: poDataToUpdate.poId,
                poDate: poDataToUpdate.poDate,
                poSubject: poSubject,
                name: poDataToUpdate.name,
                supplierAddress: poDataToUpdate.supplierAddress,
                supplierGst: poDataToUpdate.supplierGst,
                supplierName: poDataToUpdate.supplierName,
                contactName: poDataToUpdate.contactName,
                supplierMobile: poDataToUpdate.supplierMobile,
                supplierEmail: poDataToUpdate.supplierEmail,
                buyerCompanyName: poDataToUpdate.buyerCompanyName,
                projectName: poDataToUpdate.projectName,
                deliveryAddress: poDataToUpdate.deliveryAddress,
                deliveryDate: poDataToUpdate.deliveryDate,
                buyerName: poDataToUpdate.buyerName,
                buyerMobile: poDataToUpdate.buyerMobile,
                buyerEmail: poDataToUpdate.buyerEmail,
                termsAndConditions: poDataToUpdate.termsAndConditions,
                paymentTerms: hasEditedPaymentTermsRef.current
                    ? editedPaymentTermsRef.current
                    : poDataToUpdate.paymentTerms,
                otherCharges: poDataToUpdate.otherCharges,
                materialTotalExclGST: poDataToUpdate.materialTotalExclGST,
                materialTotalInclGST: poDataToUpdate.materialTotalInclGST,
                totalExclGst: poDataToUpdate.totalExclGst,
                totalInclGst: poDataToUpdate.totalInclGst,
                companyStampSign: poDataToUpdate.companyStampSign,
                updatedBy: user?.name,
                materials: (poDataToUpdate.materials || []).map((material, index) => ({
                    ...material,
                    name: Object.prototype.hasOwnProperty.call(
                        editedMaterialDescriptionsRef.current,
                        index,
                    )
                        ? editedMaterialDescriptionsRef.current[index]
                        : material.name,
                })),
            };
        } else {
            return getInitialValuesForNewPO(rfqData);
        }

    };

    const handleBack = () => {
        navigate(-1); // Navigates to the previous page
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            setLoading(true); // Start loading spinner or disable button

            let response;
            if (isEditing) {
                // Update existing PO
                response = await axios.put(`/api/rfqs/update/po`, values);

            } else {
                // Create new PO
                response = await axios.post('/api/rfqs/create/po', values);
            }

            if (response.status === 200) {
                enqueueSnackbar("PO created successfully", { variant: 'success', autoHideDuration: 3000 });

                // Save PO data to state
                const poData = response.data.payload; // Get the PO data

                // Generate PDF blob
                const poPdf = <PoPdfGenerator poData={poData} />;
                const pdfBlob = await pdf(poPdf).toBlob();

                // Trigger download
                const downloadLink = document.createElement('a');
                downloadLink.href = URL.createObjectURL(pdfBlob);
                downloadLink.download = `PO_${poData.poId}.pdf`;
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink); // Clean up
                navigate(-1); // Navigate back to the previous page

            } else {
                enqueueSnackbar("PO creation failed", { variant: 'error', autoHideDuration: 3000 });
            }
        } catch (error) {
            console.log(error);
            // Check for specific error message from backend (poId already exists)
            if (error.response && error.response.data && error.response.data.message === "PO Number already exists") {
                enqueueSnackbar("PO ID already exists. Please use a different Number.", { variant: 'error', autoHideDuration: 3000 });
            } else {
                enqueueSnackbar("Something went wrong while creating PO", { variant: 'error', autoHideDuration: 3000 });
            }
        } finally {
            setLoading(false); // Reset loading state
            setSubmitting(false); // Reset form submission state
        }
    };


    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        window.addEventListener("click", handleClickOutside);
        return () => {
            window.removeEventListener("click", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (rfqData && rfqData.suppliers && rfqData.suppliers.length > 0) {
            const supplierId = rfqData.suppliers[0].supplierId;
            const isNeevayVendor = rfqData.suppliers[0].isNeevayVendor;
            const entityId = rfqData.entityId; // Use the entityId from rfqData

            // Call the API to fetch vendor details
            const fetchVendorDetails = async () => {
                try {
                    // Send entityId and supplierId as query parameters in the API request
                    const response = await axios.get(`/api/directory/vendor-data`, {
                        params: {
                            entityId,
                            supplierId,
                            isNeevayVendor
                        }
                    });

                    if (response.data && !response.data.error) {
                        const { gstNumber, supplierAddress } = response.data.payload;
                        setSupplierGst(gstNumber); // Set GST Number
                        setSupplierAddress(supplierAddress); // Set Supplier Address
                    } else {
                        // enqueueSnackbar("Failed to fetch supplier details", { variant: "error" });
                    }
                } catch (error) {
                    // enqueueSnackbar("Error fetching supplier details", { variant: "error" });
                }
            };

            fetchVendorDetails();
        }
    }, [rfqData]);


    useEffect(() => {
        const fetchProjectDetails = async () => {
            const { projectId } = rfqData;

            if (projectId) {
                try {
                    const response = await axios.get(`/api/entity/project`, { params: { projectId } });

                    if (response.data && !response.data.error) {
                        const { deliveryAddress, buyerName, buyerMobile } = response.data.payload;

                        setDeliveryAddress( poDataToUpdate ? poDataToUpdate?.deliveryAddress : deliveryAddress);
                        setBuyerName(buyerName);
                        setBuyerMobile(buyerMobile);
                    } else {
                        // enqueueSnackbar("Failed to fetch project details", { variant: "error" });
                    }
                } catch (error) {
                    // enqueueSnackbar("Error fetching project details", { variant: "error" });
                }
            }
        };

        if (rfqData?.projectId) {
            fetchProjectDetails();
        }
    }, [rfqData?.projectId]);

    useEffect(() => {
        if (poDataToUpdate && poDataToUpdate.poSubject) {
            setPoSubject(poDataToUpdate.poSubject);
        }
    }, [poDataToUpdate]);



    return (
        <SidePanel>
            <div className="max-w-5xl text-sm mx-auto p-6 bg-white">
                <div className="grid grid-cols-5">
                    <button
                        onClick={handleBack}
                        className="mb-4 px-4 py-1 col-span-2 w-fit hover:bg-gray-100 border border-black"
                    >
                        Back
                    </button>
                    <h2 className="text-2xl col-span-3 font-semibold underline mb-4">PRCHASE ORDER</h2>
                </div>
                {/* <div>
                    <p className="text-center text-lg pb-4">
                        <b>{rfqData?.userBusinessName}</b> <br /> {rfqData?.location}
                    </p>
                </div> */}
                <div className="border-black border space-y-4 p-4">

                    {/* Supplier & Order Details */}
                    <Formik enableReinitialize initialValues={getInitialValues(rfqData, poDataToUpdate)} validationSchema={validationSchema} onSubmit={handleSubmit}>
                        {({ values, setFieldValue, isSubmitting, touched, errors }) => {

                            const poId1 = poId || "";
                            const poIdParts = poId1.split('/');
                            const prefix = poIdParts.length > 1 ? poIdParts.slice(0, -1).join('/') + '/' : '';
                            const number = poIdParts.pop();

                            const handleQuantityChange = (index, newQuantity) => {
                                const updatedMaterials = [...values.materials];
                                updatedMaterials[index].quantity = newQuantity;

                                // Recalculate totalExclGst (Rate * Quantity)
                                updatedMaterials[index].totalExclGst = updatedMaterials[index].rate * newQuantity;

                                // Update the Formik field value for the material
                                setFieldValue("materials", updatedMaterials);

                                // console.log("updatedMaterials", updatedMaterials)

                                // Recalculate the totals after quantity change
                                const materialTotalExclGST = updatedMaterials.reduce((acc, material) => acc + (material.rate * material.quantity), 0);
                                const materialTotalInclGST = updatedMaterials.reduce((acc, material) => acc + (material.rate * material.quantity * (1 + (material.gst / 100))), 0);

                                const totalExclGst = materialTotalExclGST + Object.values(values.otherCharges).reduce((acc, charge) => acc + charge, 0);
                                const totalInclGst = materialTotalInclGST + Object.entries(values.otherCharges).reduce((acc, [key, charge]) => {
                                    const chargeGst = lastQuotationSet?.otherCharges?.[key]?.gst || 18;
                                    return acc + (charge * (1 + chargeGst / 100));
                                }, 0);

                                console.log("materialTotalExclGST", materialTotalExclGST)
                                console.log("materialTotalInclGST", materialTotalInclGST)
                                console.log("totalExclGst", totalExclGst)
                                console.log("totalInclGst", totalInclGst)

                                // Update the totals in the form
                                setFieldValue("materialTotalExclGST", materialTotalExclGST);
                                setFieldValue("materialTotalInclGST", materialTotalInclGST);
                                setFieldValue("totalExclGst", totalExclGst);
                                setFieldValue("totalInclGst", totalInclGst);
                            };


                            return (
                                <Form>
                                    <PreserveEditedPaymentTerms
                                        editedPaymentTermsRef={editedPaymentTermsRef}
                                        hasEditedPaymentTermsRef={hasEditedPaymentTermsRef}
                                        setFieldValue={setFieldValue}
                                        values={values}
                                    />
                                    <div className="space-y-8">
                                        <div className="grid grid-cols-2 space-x-10 gap-4">
                                            <div className="flex flex-col  text-sm space-y-2">
                                                <h3 className="font-semibold mb-2">To:</h3>

                                                {/* Supplier Name */}
                                                <div className="flex space-x-1 text-sm">
                                                    <label className="w-1/4">Supplier Company</label>
                                                    <p>: </p>
                                                    <div className="flex-1">
                                                        <ReactQuill
                                                            name="supplierName"
                                                            value={values.supplierName}
                                                            onChange={(value) => setFieldValue(`supplierName`, value)}  // Update the correct field in the array
                                                            theme="snow"
                                                            className="text-xs word-break p-0 py-0 px-0 focus:outline-none focus:ring-0 bg-white w-full remove-quill-padding"
                                                            modules={{ toolbar: false }}
                                                        />
                                                        <ErrorMessage name="supplierName" component="div" className="text-red-500 text-xs" />
                                                    </div>
                                                </div>
                                                {/* Supplier Address */}
                                                <div className="flex space-x-1 text-sm">
                                                    <label className="w-1/4">Supplier Address</label>
                                                    <p>: </p>
                                                    <div className="flex-1">
                                                        <ReactQuill
                                                            name="supplierAddress"
                                                            value={values.supplierAddress || ""}
                                                            onChange={(value) => {
                                                                setSupplierAddress(value);
                                                                setFieldValue(`supplierAddress`, value)
                                                            }}
                                                            theme="snow"
                                                            className="text-xs word-break h-[130px] p-0 py-0 px-0 focus:outline-none focus:ring-0 bg-white w-full remove-quill-padding"
                                                            modules={{ toolbar: false }}
                                                        />
                                                        <ErrorMessage name="supplierAddress" component="div" className="text-red-500 text-xs" />
                                                    </div>
                                                </div>
                                                {/* Supplier GST */}
                                                <div className="flex space-x-1 text-sm">
                                                    <label className="w-1/4">Supplier GST</label>
                                                    <p>: </p>
                                                    <div className="flex-1">
                                                        <ReactQuill
                                                            name="supplierGst"
                                                            value={values.supplierGst || ""}
                                                            onChange={(value) => {
                                                                setFieldValue(`supplierGst`, value);
                                                                setSupplierGst(value)
                                                            }}
                                                            theme="snow"
                                                            className="text-xs word-break p-0 py-0 px-0 focus:outline-none focus:ring-0 bg-white w-full remove-quill-padding"
                                                            modules={{ toolbar: false }}
                                                        />
                                                        <ErrorMessage name="supplierGst" component="div" className="text-red-500 text-xs" />
                                                    </div>
                                                </div>

                                                {/* name */}
                                                <div className="flex space-x-1">
                                                    <label className="w-1/4">Contact Name</label>
                                                    <p>: </p>
                                                    <div className="flex-1">
                                                        <ReactQuill
                                                            name="contactName"
                                                            value={values.contactName}
                                                            onChange={(value) => {
                                                                setContactName(value)
                                                                setFieldValue(`contactName`, value)
                                                            }}
                                                            theme="snow"
                                                            className="text-xs word-break p-0 py-0 px-0 focus:outline-none focus:ring-0 bg-white w-full remove-quill-padding"
                                                            modules={{ toolbar: false }}
                                                        />
                                                        <ErrorMessage name="contactName" component="div" className="text-red-500 text-xs" />
                                                    </div>
                                                </div>

                                                {/* Contact No */}
                                                <div className="flex space-x-1">
                                                    <label className="w-1/4">Contact No.</label>
                                                    <p>: </p>
                                                    <div className="flex-1">
                                                        <Field
                                                            name="supplierMobile"
                                                            type="text"
                                                            className=" w-full border border-gray-300 px-2 py-1 text-sm focus:ring-0"
                                                            placeholder="Enter contact number"
                                                        />
                                                        <ErrorMessage name="supplierMobile" component="div" className="text-red-500 text-xs" />
                                                    </div>
                                                </div>

                                                {/* Email */}
                                                <div className="flex space-x-1">
                                                    <label className="w-1/4">Contact Email</label>
                                                    <p>: </p>
                                                    <div className="flex-1">
                                                        <ReactQuill
                                                            name="supplierEmail"
                                                            value={values.supplierEmail}
                                                            onChange={(value) => setFieldValue(`supplierEmail`, value)}  // Update the correct field in the array
                                                            theme="snow"
                                                            className="text-xs word-break p-0 py-0 px-0 focus:outline-none focus:ring-0 bg-white w-full remove-quill-padding"
                                                            modules={{ toolbar: false }}
                                                        />
                                                        <ErrorMessage name="supplierEmail" component="div" className="text-red-500 text-xs" />
                                                    </div>
                                                </div>
                                            </div>


                                            {/* Static Fields */}
                                            <div className="grid grid-cols-3 gap-2 text-sm">
                                                <h3 className="font-semibold mb-2 col-span-3">From:</h3>

                                                <label className="">Company Name</label>
                                                <div className="col-span-2 space-x-1 flex">
                                                    <p>:</p>
                                                    {/* Company dropdown logic stays the same */}
                                                    <div className="relative w-full">
                                                        <div className="relative w-full" ref={dropdownRef}>
                                                            <input
                                                                type="text"
                                                                className="w-full border border-gray-300 cursor-pointer focus:ring-0 px-2 py-1 text-sm"
                                                                value={values.companyName || query}
                                                                onChange={(e) => {
                                                                    setQuery(e.target.value);
                                                                    setShowDropdown(true);
                                                                }}
                                                                onFocus={() => setShowDropdown(true)}
                                                                placeholder="Select company name..."
                                                                disabled={isEditing}
                                                            />

                                                            {!isEditing && <img
                                                                src="https://storagereponeevaydevcdn.blob.core.windows.net/business/create_rfq_dropdown_arrow.svg"
                                                                alt="Dropdown Arrow"
                                                                className={`absolute right-2 top-1/2 bg-white cursor-pointer h-3 w-3 transform -translate-y-1/2 transition-transform duration-300 ${showDropdown ? 'rotate-180' : 'rotate-0'}`}
                                                                onClick={() => setShowDropdown((prev) => !prev)}
                                                            />}
                                                        </div>

                                                        {showDropdown && (
                                                            <ul className="absolute z-10 bg-white border mt-1 w-full max-h-36 overflow-y-auto rounded shadow">
                                                                {filteredCompanies.length > 0 ? (
                                                                    filteredCompanies.map((company, index) => (
                                                                        <li
                                                                            key={index}
                                                                            className="py-1 px-2 hover:bg-blue-100 cursor-pointer"
                                                                            onClick={() => {
                                                                                handleSelect(company, setFieldValue);
                                                                                setFieldValue('buyerCompanyName', company);
                                                                            }}
                                                                        >
                                                                            {company}
                                                                        </li>
                                                                    ))
                                                                ) : (
                                                                    <li className="p-2 text-gray-500">No results</li>
                                                                )}
                                                            </ul>
                                                        )}
                                                        {errors.buyerCompanyName && (
                                                            <div className="text-red-500 text-sm">{errors.buyerCompanyName}</div>
                                                        )}
                                                    </div>
                                                </div>

                                                {
                                                    isEditing ? (
                                                        <>
                                                            <p className="text-left">PO Number</p>
                                                            <p className="col-span-2">: {values?.poId}</p>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <p className="text-left">PO Number</p>
                                                            <div className="col-span-2 flex flex-col space-y-1">
                                                                <div className="flex items-center space-x-1">
                                                                    <span className="whitespace-nowrap">: {prefix}</span>
                                                                    {prefix && (
                                                                        <Field
                                                                            name="poNumber"
                                                                            type="text"
                                                                            className="w-16 border border-gray-300 focus:ring-0 px-1 py-0.5 text-sm"
                                                                            placeholder="Number"
                                                                            value={number}
                                                                            onChange={(e) => {
                                                                                setPoId(prefix + e.target.value);
                                                                            }}
                                                                        // onChange={e => setFieldValue('poId', prefix + e.target.value)}
                                                                        />
                                                                    )}
                                                                </div>
                                                                {/* Error message aligned directly below the input */}
                                                                {(poId && !number) && (
                                                                    <div className="text-red-500 text-sm right-0">Required</div>
                                                                )}
                                                            </div>
                                                        </>
                                                    )
                                                }



                                                <p className="text-left">PO Date</p>
                                                <p className="col-span-2">: {values?.poDate}</p>

                                                <p className="text-left">Project Name</p>
                                                <p className="col-span-2">: {values?.projectName}</p>

                                                <p className="text-left">Delivery Address</p>
                                                <p className="col-span-2 flex">
                                                    :
                                                    <ReactQuill
                                                        value={values.deliveryAddress || ""}
                                                        onChange={(value) => {
                                                            setFieldValue("deliveryAddress", value);
                                                            setDeliveryAddress(value);
                                                        }}
                                                        theme="snow"
                                                        className="text-xs word-break ml-1 p-0 py-0 px-0 focus:outline-none focus:ring-0 bg-white w-full remove-quill-padding"
                                                        modules={{ toolbar: false }}
                                                    />
                                                </p>

                                                <p className="text-left">Delivery Date</p>
                                                <p className="col-span-2 flex">
                                                    :
                                                    <Field
                                                        name="deliveryDate"
                                                        type="date"
                                                        onChange={(e) => {
                                                            setFieldValue("deliveryDate", e.target.value);
                                                            setDeliveryDate(e.target.value);
                                                        }}
                                                        className="ml-1 w-full border border-gray-300 px-2 py-1 text-sm focus:ring-0"
                                                    />
                                                </p>

                                                <p className="text-left">Site Contact Person</p>
                                                <p className="col-span-2 flex">
                                                    :
                                                    <ReactQuill
                                                        name="buyerName"
                                                        value={values.buyerName}
                                                        onChange={(value) => {
                                                            setBuyerName(value);
                                                            setFieldValue(`buyerName`, value)
                                                        }}
                                                        theme="snow"
                                                        className="text-xs word-break ml-1 p-0 py-0 px-0 focus:outline-none focus:ring-0 bg-white w-full remove-quill-padding"
                                                        modules={{ toolbar: false }}
                                                    />
                                                </p>

                                                <p className="text-left">Site Contact No.</p>
                                                <p className="col-span-2 flex">
                                                    :
                                                    <Field
                                                        name="buyerMobile"
                                                        type="text"
                                                        className="ml-1 w-full border border-gray-300 px-2 py-1 text-sm focus:ring-0"
                                                        placeholder="Enter contact number"
                                                        onChange={(e) => {
                                                            setBuyerMobile(e.target.value);
                                                            setFieldValue(`buyerMobile`, e.target.value)
                                                        }}
                                                    />
                                                </p>

                                                {/* <p className="text-left">Email</p>
                                                <p className="col-span-2 flex">
                                                    :
                                                    <ReactQuill
                                                        name="buyerEmail"
                                                        value={values.buyerEmail}
                                                        onChange={(value) => setFieldValue(`buyerEmail`, value)}
                                                        theme="snow"
                                                        className="text-xs word-break ml-1 p-0 py-0 px-0 focus:outline-none focus:ring-0 bg-white w-full remove-quill-padding"
                                                        modules={{ toolbar: false }}
                                                    />
                                                </p> */}
                                            </div>
                                        </div>

                                        {/* po subject */}
                                        <div className="space-y-4 w-full">
                                            <div className=" border-black">
                                                <h3 className="font-semibold text-lg mb-2">Headline :</h3>
                                                <Field
                                                    name="poSubject"
                                                    type="text"
                                                    className="text-sm border max-h-[80px] overflow-y-auto w-full h-[30px] focus:ring-0 px-1"
                                                    value={poSubject}
                                                    onChange={(e) => {
                                                        setPoSubject(e.target.value);
                                                        setFieldValue("poSubject", e.target.value);
                                                    }}
                                                />

                                            </div>
                                        </div>

                                        {/* Items Table */}
                                        <div>
                                            <h3 className="mb-4 text-lg font-semibold">Material List & Cost Components</h3>
                                            {values?.materials && values?.materials.length > 0 && (
                                                <table className="w-full border text-sm border-gray-300">
                                                    <thead>
                                                        <tr className="border border-black">
                                                            <th className="border border-black p-2">Sr#</th>
                                                            <th className="border border-black p-2">Item & Description</th>
                                                            <th className="border border-black p-2">Unit</th>
                                                            <th className="border border-black p-2">Qty</th>
                                                            <th className="border border-black p-2">Rate</th>
                                                            <th className="border border-black p-2">GST</th>
                                                            <th className="border border-black p-2">Amount</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {values?.materials?.map((material, index) => {

                                                            return (
                                                                <tr key={index}>
                                                                    <td className="border border-black text-center p-1">{index + 1}</td>

                                                                    {/* Editable Combined Text */}
                                                                    <td className="border border-black p-1">

                                                                        <ReactQuill
                                                                            name={`materials[${index}].name`}
                                                                            value={values.materials[index].name || ''}  // Access the actual value
                                                                            onChange={(value) => {
                                                                                editedMaterialDescriptionsRef.current[index] = value;
                                                                                setFieldValue(`materials[${index}].name`, value);
                                                                            }}
                                                                            theme="snow"
                                                                            className="text-xs word-break p-0 py-0 px-0 focus:outline-none focus:ring-0 bg-white w-full remove-quill-padding"
                                                                            modules={{ toolbar: false }}
                                                                        />
                                                                    </td>


                                                                    {/* Static Unit */}
                                                                    <td className="border border-black text-center text-sm p-1">{material.unit}</td>

                                                                    {/* Editable Quantity */}
                                                                    <td className="border border-black text-center p-1">
                                                                        <Field
                                                                            name={`materials[${index}].quantity`}
                                                                            type="text"
                                                                            className="text-sm border w-[10ch] h-[30px] focus:ring-0 px-1 text-center"
                                                                            min="0"
                                                                            onChange={(e) => handleQuantityChange(index, e.target.value)}
                                                                        />
                                                                    </td>
                                                                    <td className="border border-black text-center text-sm p-1">
                                                                    {material?.rate 
                                                                        ? new Intl.NumberFormat('en-IN').format(Number(material?.rate).toFixed(2)) 
                                                                        : "--"}
                                                                    </td>
                                                                    <td className="border border-black text-center text-sm p-1">{material.gst || "--"}</td>
                                                                    <td className="border border-black text-center text-sm p-1">
                                                                    {material?.totalExclGst 
                                                                        ? new Intl.NumberFormat('en-IN').format(Number(material?.totalExclGst).toFixed(2)) 
                                                                        : "--"}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}


                                                    </tbody>
                                                </table>
                                            )}
                                        </div>

                                        {/* Charges & Totals section */}
                                        <div>
                                            {values?.otherCharges && Object.entries(values.otherCharges).length > 0 && (
                                                <h3 className="mt-4 text-lg font-semibold">Other Charges</h3>
                                            )}
                                            <div className="flex justify-between">
                                                {/* Left Section: Other Charges */}
                                                <div className="grid grid-cols-2 mt-2 gap-2">
                                                    {values.otherCharges &&
                                                        Object.entries(values?.otherCharges).map(([key, value]) => (
                                                            <React.Fragment key={key}>
                                                                <p className="capitalize">{key.replace(/([A-Z])/g, " $1")}</p>
                                                                <p>: Rs.{new Intl.NumberFormat("en-IN").format(value.toFixed(2))}</p>
                                                            </React.Fragment>
                                                        ))}
                                                    {/* GST Applicable */}
                                                    {/* {values?.otherCharges && Object?.entries(values?.otherCharges).length > 0 && <>
                                                        <p className="capitalize">GST Applicable</p>
                                                        <p>: 18%</p>
                                                    </>} */}
                                                </div>

                                                {/* Right Section: Totals */}
                                                <div className="space-y-2 mr-4">
                                                    <p>
                                                        Material Amount: Rs.
                                                        {new Intl.NumberFormat("en-IN").format((values?.materialTotalExclGST).toFixed(2)) || 0}
                                                    </p>
                                                    <p>
                                                        Other Charges: Rs.
                                                        {new Intl.NumberFormat("en-IN").format(
                                                            (Object.values(values?.otherCharges || {}).reduce((acc, charge) => acc + charge, 0)).toFixed(2)
                                                        )}
                                                    </p>
                                                    <p>
                                                        Total GST: Rs.
                                                        {new Intl.NumberFormat("en-IN").format((values?.totalInclGst - values.totalExclGst).toFixed(2)) || 0}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>


                                        <div className="border-y border-black">
                                            <h2 className="py-2 pr-4 w-full text-lg font-semibold text-right">Total Order Value (TOV) : Rs.{new Intl.NumberFormat('en-IN').format((values.totalInclGst).toFixed(2)) || 0}</h2>
                                        </div>

                                        {/* Terms & Conditions */}
                                        <div className="space-y-4 w-full">
                                            <div className=" border-black">
                                                <h3 className="font-semibold text-lg mb-2">Terms & Conditions :</h3>

                                                <ReactQuill
                                                    name="termsAndConditions"
                                                    value={values.termsAndConditions}
                                                    onChange={(value) => {
                                                        setTermsAndConditions(value)
                                                        setFieldValue('termsAndConditions', value);
                                                    }}
                                                    theme="snow"
                                                    className="text-xs word-break h-[100px] p-0 py-0 px-0 focus:outline-none focus:ring-0 bg-white border-[#DCDCD0] border w-full remove-quill-padding"
                                                    modules={{ toolbar: false }}
                                                />
                                            </div>
                                            <div className="">
                                                <div className=" border-black">
                                                    <h3 className="font-semibold text-lg mb-2">Payment Terms :</h3>
                                                    <ReactQuill
                                                        name="paymentTerms"
                                                        value={values.paymentTerms}
                                                        onChange={(value) => {
                                                            hasEditedPaymentTermsRef.current = true;
                                                            editedPaymentTermsRef.current = value;
                                                            setFieldValue('paymentTerms', value);
                                                        }}
                                                        theme="snow"
                                                        className="text-xs word-break h-[100px] p-0 py-0 px-0 focus:outline-none focus:ring-0 bg-white border-[#DCDCD0] border w-full remove-quill-padding"
                                                        modules={{ toolbar: false }}
                                                    />
                                                </div>

                                                {/* {companyStampSign && (
                                                    <div className="p-4 border-t border-black">
                                                        <h3 className="font-semibold text-lg mb-2">Authorized Signature:</h3>
                                                        <img src={companyStampSign} alt="Authorized Signature" className="h-20" />
                                                    </div>
                                                )} */}
                                            </div>
                                        </div>


                                        {/* Submit button */}
                                        <div className="rounded-b-lg bg-white w-full space-x-4 flex justify-end mt-4">
                                            <button
                                                type="button"
                                                onClick={handleBack}
                                                className=" px-4 py-1 w-fit hover:bg-gray-100 border border-black"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={isSubmitting || loading || !number}
                                                className={`px-4 py-1 ${isSubmitting || loading ? 'bg-gray-500' : 'bg-blue-500'} text-white hover:bg-white border border-blue-500 hover:text-blue-500`}
                                            >
                                                {isEditing ? "Update PO" : "Create PO"}
                                            </button>

                                        </div>
                                    </div>
                                </Form>
                            )

                        }}
                    </Formik>
                </div>

                {/* <div className="flex justify-between p-2 text-xs">
                <div>
                    <p>Prepared By:</p>
                    <p>Contact No.:</p>
                    <p>Neevay ID:</p>
                </div>
                <p>Page 1 of 1</p>
            </div> */}
            </div>

            {isPDFViewerOpen && poData && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg w-[75%] h-[98%] flex flex-col z-999">
                        <div className="flex justify-between items-center p-2 border-b">
                            <h2 className="text-base font-bold">PO PDF Preview - {poData?.poId}</h2>
                            <button
                                onClick={handleClosePDFViewer}
                                className="text-red-600 hover:text-red-800 font-semibold"
                            >
                                Close
                            </button>
                        </div>
                        <div className="flex-grow overflow-auto z-70 relative">
                            <PDFViewer width="100%" height="100%" className="h-full w-full">
                                <PoPdfGenerator poData={poData} companyStampSign={companyStampSign} />
                            </PDFViewer>
                        </div>
                        <div className="p-2 border-t">

                            <PDFDownloadLink
                                document={<PoPdfGenerator poData={poData} companyStampSign={companyStampSign} />}
                                fileName={`PO_${poData?.poId || "Export"}.pdf`}
                            >
                                {({ loading }) =>
                                    loading ? (
                                        <button className="bg-black text-white px-4 py-2 rounded ">
                                            Preparing PDF...
                                        </button>
                                    ) : (
                                        <button className="bg-black text-white px-4 py-1 rounded ">
                                            Download PDF
                                        </button>
                                    )
                                }
                            </PDFDownloadLink>
                        </div>
                    </div>
                </div>
            )}

        </SidePanel>

    );
};

export default PurchaseOrderForm;
